import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, Check, PackageX } from "lucide-react";
import { toast } from "sonner";
import api, { imgUrl, formatPrice } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import { catLabel, productName, productDesc } from "../lib/i18n";

const splitOpts = (s) => (s ? s.split(",").map((x) => x.trim()).filter(Boolean) : []);

const ProductDetail = () => {
  const { id } = useParams();
  const { t, lang } = useLanguage();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  // null = hələ yüklənir, true = məhsul yoxdur
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");

  useEffect(() => {
    setNotFound(false);
    setProduct(null);
    api.get(`/products/${id}`)
      .then(({ data }) => {
        setProduct(data);
        setActive(0);
        setQty(1);
        setSize("");
        setColor("");
      })
      // Məhsul silinibsə və ya link səhvdirsə səhifə sonsuz "Yüklənir..."
      // qalmamalıdır — müştəri saytın sındığını düşünər.
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="product-not-found">
        <PackageX size={48} strokeWidth={1} className="mx-auto text-brand-muted" />
        <h1 className="font-serif font-light text-4xl tracking-tight mt-8">
          {t("product_gone_title")}
        </h1>
        <p className="mt-5 text-brand-sub font-light">{t("product_gone_sub")}</p>
        <div className="mt-10 flex flex-wrap gap-3 justify-center">
          <Link
            to="/shop"
            data-testid="gone-to-shop"
            className="bg-brand-ink text-white px-8 py-4 text-sm tracking-[0.12em] uppercase hover:bg-brand-oxid transition-colors"
          >
            {t("view_all")}
          </Link>
          <Link
            to="/"
            className="border border-brand-line px-8 py-4 text-sm tracking-[0.12em] uppercase hover:border-brand-ink transition-colors"
          >
            {t("back_home")}
          </Link>
        </div>
      </div>
    );
  }

  if (!product) return <div className="max-w-7xl mx-auto px-6 py-32 text-brand-muted">{t("loading")}</div>;

  const isClothing = product.group === "clothing";
  // Ölçülü məhsul: hər ölçünün öz stoku var. Ölçüsüz məhsulda köhnə davranış.
  const variants = product.variants || [];
  const hasVariants = variants.length > 0;
  const sizeOpts = hasVariants ? variants.map((v) => v.size) : splitOpts(product.size);
  const colorOpts = splitOpts(product.color);
  const soldOut = (product.stock ?? 0) <= 0;
  // Seçilmiş ölçünün stoku — miqdar limiti buna görə qoyulur.
  const variantStock = (s) =>
    hasVariants ? (variants.find((v) => v.size === s)?.stock ?? 0) : (product.stock ?? 0);
  const availableStock = hasVariants
    ? (size ? variantStock(size) : 0)
    : (product.stock ?? 0);
  const images = product.images && product.images.length ? product.images : [null];

  const handleAdd = () => {
    if (soldOut) return;
    // Ölçülü məhsulda ölçü seçmədən səbətə atmaq olmaz — hansı ölçünün
    // stokundan tutulacağı bəlli olmalıdır.
    if (hasVariants && !size) {
      toast.error(lang === "en" ? "Please select a size" : "Zəhmət olmasa ölçü seçin");
      return;
    }
    if (hasVariants && variantStock(size) <= 0) {
      toast.error(lang === "en" ? "This size is sold out" : "Bu ölçü satılıb");
      return;
    }
    if (!hasVariants && isClothing && sizeOpts.length > 1 && !size) {
      toast.error(lang === "en" ? "Please select a size" : "Zəhmət olmasa ölçü seçin");
      return;
    }
    if (isClothing && colorOpts.length > 1 && !color) {
      toast.error(lang === "en" ? "Please select a color" : "Zəhmət olmasa rəng seçin");
      return;
    }
    addItem(product, qty, {
      size: hasVariants
        ? size
        : (isClothing ? size || sizeOpts[0] || "" : product.size),
      color: isClothing ? color || colorOpts[0] || "" : product.color,
      // Səbətdəki limit ümumi stoka yox, SEÇİLMİŞ ölçünün stokuna görədir.
      stock: availableStock,
    });
    toast.success(lang === "en" ? "Added to cart" : "Səbətə əlavə edildi");
  };

  const detailRows = isClothing
    ? [
        [t("material"), product.material],
        [t("size"), product.size],
        [t("color"), product.color],
      ]
    : [
        [t("material"), product.material],
        // Gümüşdə çəki alıcının ən çox soruşduğu məlumatdır.
        [t("weight"), product.weight ? `${product.weight} q` : ""],
        [t("size"), product.size],
        [t("length"), product.length],
        [t("clasp"), product.clasp],
      ];

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="product-detail-page">
      <p className="overline mb-8">
        <Link to="/shop" className="hover:text-brand-ink">{t("nav_all")}</Link> /{" "}
        <Link to={`/shop?category=${encodeURIComponent(product.category)}`} className="hover:text-brand-ink">
          {catLabel(product.category, lang)}
        </Link>
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        {/* Gallery */}
        <div className="flex flex-col-reverse sm:flex-row gap-4">
          {images.length > 1 && (
            <div className="flex sm:flex-col gap-3">
              {images.map((im, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`w-16 h-20 overflow-hidden border ${active === i ? "border-brand-ink" : "border-brand-line"}`}
                  data-testid={`thumb-${i}`}
                >
                  <img src={imgUrl(im)} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.src = imgUrl(null); }} />
                </button>
              ))}
            </div>
          )}
          <motion.div
            key={active}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="flex-1 aspect-[4/5] overflow-hidden bg-brand-bg2"
          >
            <img src={imgUrl(images[active])} alt={productName(product, lang)} className="w-full h-full object-cover" onError={(e) => { e.target.src = imgUrl(null); }} />
          </motion.div>
        </div>

        {/* Info */}
        <div className="lg:pt-6">
          <p className="overline mb-3">{catLabel(product.category, lang)}</p>
          <h1 className="font-serif font-light text-4xl sm:text-5xl tracking-tight leading-tight" data-testid="product-name">
            {productName(product, lang)}
          </h1>
          <p className="mt-2 text-xs text-brand-muted font-mono">{t("code")}: {product.code}</p>
          <p className="mt-6 font-serif text-3xl text-brand-ink" data-testid="product-price">{formatPrice(product.price)}</p>

          {productDesc(product, lang) && (
            <p className="mt-6 text-brand-sub font-light leading-relaxed max-w-md">{productDesc(product, lang)}</p>
          )}

          {/* Ölçü seçimi. Ölçülü məhsulda hər düymənin altında qalıq sayı yazılır —
              müştəri "M var, L yoxdur" məlumatını dərhal görür. */}
          {(hasVariants || (isClothing && sizeOpts.length > 0)) && (
            <div className="mt-8">
              <p className="overline mb-3">{t("size")}</p>
              <div className="flex flex-wrap gap-2">
                {sizeOpts.map((s) => {
                  const left = variantStock(s);
                  const gone = hasVariants && left <= 0;
                  return (
                    <button
                      key={s}
                      onClick={() => { setSize(s); setQty(1); }}
                      disabled={gone}
                      data-testid={`size-${s}`}
                      title={gone ? t("out_of_stock") : undefined}
                      className={`min-w-[52px] px-3 py-2 text-sm border transition-colors ${
                        gone
                          ? "border-brand-line text-brand-muted line-through cursor-not-allowed"
                          : size === s
                          ? "bg-brand-ink text-white border-brand-ink"
                          : "border-brand-line hover:border-brand-ink"
                      }`}
                    >
                      {s}
                      {hasVariants && !gone && (
                        <span className={`block text-[10px] mt-0.5 ${size === s ? "text-white/70" : "text-brand-muted"}`}>
                          {left} {t("pcs")}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Clothing color selector */}
          {isClothing && colorOpts.length > 0 && (
            <div className="mt-6">
              <p className="overline mb-3">{t("color")}</p>
              <div className="flex flex-wrap gap-2">
                {colorOpts.map((c) => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    data-testid={`color-${c}`}
                    className={`px-3 py-2 text-sm border transition-colors ${color === c ? "bg-brand-ink text-white border-brand-ink" : "border-brand-line hover:border-brand-ink"}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Qty + add */}
          <div className="mt-10 flex items-center gap-4">
            <div className="flex items-center border border-brand-line">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 hover:bg-brand-bg2" data-testid="qty-dec"><Minus size={15} strokeWidth={1.5} /></button>
              <span className="px-5 text-sm" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => Math.min(availableStock || 1, q + 1))}
              disabled={qty >= (availableStock || 1)} className="p-3 hover:bg-brand-bg2" data-testid="qty-inc"><Plus size={15} strokeWidth={1.5} /></button>
            </div>
            <button
              onClick={handleAdd}
              disabled={soldOut}
              data-testid="detail-add-to-cart"
              className="flex-1 bg-brand-ink text-white py-4 text-sm tracking-[0.15em] uppercase hover:bg-brand-oxid transition-colors disabled:opacity-40"
            >
              {soldOut ? t("out_of_stock") : t("add_to_cart")}
            </button>
          </div>

          <p className={`mt-4 text-xs flex items-center gap-2 ${soldOut ? "text-destructive" : "text-brand-sub"}`}>
            {!soldOut && <Check size={14} />}
            {soldOut
              ? t("out_of_stock")
              : hasVariants && !size
              ? t("choose_size_first")
              : `${t("in_stock")} · ${availableStock} ${t("pcs")}`}
          </p>

          {/* Details table */}
          <div className="mt-12 border-t border-brand-line">
            <p className="overline mt-8 mb-4">{t("details")}</p>
            <dl className="divide-y divide-brand-line">
              {detailRows.filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="flex justify-between py-3 text-sm">
                  <dt className="text-brand-muted">{k}</dt>
                  <dd className="text-brand-ink">{v}</dd>
                </div>
              ))}
              <div className="flex justify-between py-3 text-sm">
                <dt className="text-brand-muted">{t("code")}</dt>
                <dd className="text-brand-ink font-mono">{product.code}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
